import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { paymentMethods, type PaymentMethod } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

/**
 * Meios de pagamento da empresa (P-TOM-C1, TOM-087).
 *
 * A conta bancaria do tomador escolhe o meio de pagamento desta lista, como o
 * "Selecione o meio de pagamento" do OnPoint. No OnPoint a lista e da empresa e veio
 * vazia; aqui ela nasce vazia, e nenhum meio e inventado. A conta guarda o nome do
 * meio no momento da inclusao: renomear, desativar ou remover nao reescreve as
 * contas gravadas.
 */
export function PaymentMethodsPanel() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeVer = can('configuracoes.meio_pagamento.view');
  const podeEditar = can('configuracoes.meio_pagamento.update');
  const [criando, setCriando] = useState(false);
  const [removendo, setRemovendo] = useState<PaymentMethod | null>(null);

  const consulta = useQuery({
    queryKey: ['payment-methods', 'todos'],
    queryFn: ({ signal }) => paymentMethods.list({ include_inactive: true }, signal),
    enabled: podeVer,
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
  }

  const criar = useMutation({
    mutationFn: (label: string) => paymentMethods.create({ label }),
    onSuccess: () => {
      toast.success('Meio de pagamento criado.');
      setCriando(false);
      invalidar();
    },
  });
  const alternar = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      paymentMethods.update(id, { active }),
    onSuccess: (meio) => {
      toast.success(meio.active ? 'Meio reativado.' : 'Meio desativado.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const remover = useMutation({
    mutationFn: (id: string) => paymentMethods.remove(id),
    onSuccess: () => {
      toast.success('Meio de pagamento removido.');
      setRemovendo(null);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!podeVer) return null;

  const meios = consulta.data ?? [];

  return (
    <Card>
      <CardHeader
        title="Meios de pagamento"
        description="Oferecidos na conta bancaria do tomador."
        action={
          podeEditar ? (
            <Button size="sm" variant="secondary" onClick={() => setCriando(true)}>
              Novo meio de pagamento
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-col gap-3">
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os meios de pagamento">
            {consulta.error.message}
          </Alert>
        ) : null}

        {consulta.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : meios.length === 0 ? (
          <p className="text-sm text-ink-500">
            Nenhum meio de pagamento cadastrado. Enquanto a lista estiver vazia, a conta
            do tomador e incluida sem meio de pagamento.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
            {meios.map((meio) => (
              <li key={meio.id} className="flex items-center gap-3 px-3 py-2.5">
                <span
                  className={
                    meio.active
                      ? 'min-w-0 flex-1 truncate text-sm text-ink-800'
                      : 'min-w-0 flex-1 truncate text-sm text-ink-400 line-through'
                  }
                >
                  {meio.label}
                </span>
                {!meio.active ? <Badge tone="neutral">Inativo</Badge> : null}
                {podeEditar ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={alternar.isPending}
                      onClick={() =>
                        alternar.mutate({ id: meio.id, active: !meio.active })
                      }
                    >
                      {meio.active ? 'Desativar' : 'Reativar'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Remover ${meio.label}`}
                      onClick={() => setRemovendo(meio)}
                    >
                      Remover
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        <p className="text-2xs text-ink-500">
          A lista do sistema anterior da seguradora veio vazia: os meios entram aqui
          pela empresa, e nenhum vem pronto.
        </p>
      </CardBody>

      {criando ? (
        <FormularioDeMeio
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(label) => criar.mutate(label)}
        />
      ) : null}

      {removendo ? (
        <Modal
          open
          onClose={() => setRemovendo(null)}
          title="Remover meio de pagamento"
          description={removendo.label}
          footer={
            <>
              <Button variant="secondary" onClick={() => setRemovendo(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                loading={remover.isPending}
                onClick={() => remover.mutate(removendo.id)}
              >
                Remover
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-700">
            O meio sai da lista. As contas que ja o usam continuam com o nome gravado.
          </p>
        </Modal>
      ) : null}
    </Card>
  );
}

function FormularioDeMeio({
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  submitting: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (label: string) => void;
}) {
  const [label, setLabel] = useState('');

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    onSubmit(label);
  }

  return (
    <Modal open onClose={onCancel} title="Novo meio de pagamento">
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {error.message}
          </Alert>
        ) : null}
        <Field
          label="Nome do meio de pagamento"
          required
          value={label}
          hint="Como aparece na conta bancaria do tomador."
          onChange={(evento) => setLabel(evento.target.value)}
        />
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={submitting}>
            Criar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
